import { gql, type TypedDocumentNode, useQuery } from '@apollo/client';
import type {
  InventoryTransferDestinationLotQuery,
  InventoryTransferDestinationLotQueryVariables,
} from 'types/graphql';

/**
 * The ship's rule (`inventoryTransfer.repo.ts` `findLotAtWarehouse`): units go into the lot that already holds
 * this lot number at the destination warehouse, or a new lot is created there. Same unique key, but `lot()`
 * returns active lots only — the ship's lookup also matches a soft-deleted lot, which this cannot predict.
 */
const INVENTORY_TRANSFER_DESTINATION_LOT_QUERY: TypedDocumentNode<
  InventoryTransferDestinationLotQuery,
  InventoryTransferDestinationLotQueryVariables
> = gql`
  query InventoryTransferDestinationLotQuery($slug: String!, $warehouseId: Int!) {
    lot(slug: $slug, warehouseId: $warehouseId) {
      id
      slug
      warehouseId
    }
  }
`;

/**
 * The lot a draft row's units will be added to when the transfer ships. `isResolved` stays false until
 * the server answers, so a row never claims a new lot before the lookup says there is none.
 */
export function useFindTransferDestinationLot({ slug, warehouseId }: { slug: string; warehouseId: number }) {
  const { data } = useQuery(INVENTORY_TRANSFER_DESTINATION_LOT_QUERY, {
    variables: { slug, warehouseId },
    // A preview: grid cells remount as rows scroll, so ask once per lot number per page load. The ship
    // decides the real lot, and a reload refreshes the answer.
    fetchPolicy: 'cache-first',
  });

  return { destinationLot: data?.lot ?? null, isResolved: data !== undefined };
}
